import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo } from 'react'
import { DefaultValues, FieldValues, Path, UseFormReturn } from 'react-hook-form'

import {
  CreateSearchFilterForUserBody,
  SearchFilterResourceTypeParamParameter,
  useCreateSearchFilterForUser,
  useDeleteSearchFilterForUser,
  useListSearchFilters,
} from '@api/orval/apiHooks'
import { emitToast, successBusProps } from '@lib/emitToast'
import { transformFilters } from '@lib/filtersToFormValues'
import { normalizeFiltersToObject } from '@lib/transformFiltersToParams'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { selectFetched, selectSavedFilters, setSavedFilters } from '@store/modules/filters/savedFiltersSlice'

interface UseSavedFiltersProps<T extends FieldValues> {
  form: UseFormReturn<T>
  filtersArea: SearchFilterResourceTypeParamParameter
  filterName: string
  setFilterName: (name: string) => void
  setIsModalOpen: (isOpen: boolean) => void
  initialValues: DefaultValues<T>
}

/*
  useSavedFilters hook used for: 
  - get saved filters (savedFilters)
  - fetch saved filters (fetchSavedFilters)
  - save filter (handleSaveFilter)
  - delete filter (handleDeleteFilter)
  - handle saved filter change (handleSavedFilterChange)

  Example:
  const { handleSaveFilter, handleDeleteFilter, savedFilters, handleSavedFilterChange } = useSavedFilters({
    form,
    filtersArea,
    filterName,
    setFilterName,
    setIsModalOpen,
    initialValues,
  })
*/
export function useSavedFilters<T extends FieldValues>({
  form,
  filtersArea,
  filterName,
  setFilterName,
  setIsModalOpen,
  initialValues,
}: UseSavedFiltersProps<T>) {
  const { replace } = useRouter()
  const dispatch = useAppDispatch()

  const savedFiltersSelector = useMemo(() => selectSavedFilters(filtersArea), [filtersArea])
  const savedFilters = useAppSelector(savedFiltersSelector)
  const hasFetchedSelector = useMemo(() => selectFetched(filtersArea), [filtersArea])
  const hasFetched = useAppSelector(hasFetchedSelector)

  const { data: savedFiltersData, refetch: refetchSavedFilters } = useListSearchFilters(
    { resource_type: filtersArea },
    {
      query: {
        enabled: !hasFetched,
      },
    },
  )

  useEffect(() => {
    if (savedFiltersData?.collection) {
      dispatch(setSavedFilters({ filtersArea, savedFilters: savedFiltersData.collection }))
    }
  }, [savedFiltersData, dispatch, filtersArea])

  const { mutateAsync: createFilter } = useCreateSearchFilterForUser({
    mutation: {
      onSuccess: async () => {
        const { data: updatedFilters } = await refetchSavedFilters()

        if (updatedFilters?.collection) {
          dispatch(setSavedFilters({ filtersArea, savedFilters: updatedFilters.collection }))
        }

        setIsModalOpen(false)
        setFilterName('')
        emitToast({ ...successBusProps, message: 'Successfully saved filter.' })
      },
    },
  })

  const { mutateAsync: deleteFilter } = useDeleteSearchFilterForUser({
    mutation: {
      onSuccess: (_, variables) => {
        const updated = savedFilters.filter((filter) => filter.id !== variables.id)
        dispatch(setSavedFilters({ filtersArea, savedFilters: updated }))

        emitToast({ ...successBusProps, message: 'Successfully deleted filter.' })
      },
    },
  })

  const handleSaveFilter = useCallback(async () => {
    const data = form.getValues()
    const allParams = normalizeFiltersToObject(data)

    const requestBody: CreateSearchFilterForUserBody = {
      filter: {
        name: filterName,
        filters: {
          ...allParams,
        },
      },
    }

    try {
      await createFilter({
        params: { resource_type: filtersArea },
        data: requestBody,
      })
    } catch (error) {
      console.error('Error saving filter:', error)
    }
  }, [filtersArea, form, filterName, createFilter])

  const handleDeleteFilter = useCallback(
    async (id: number) => {
      try {
        const selectedFilterId = form.getValues('saved_filters' as Path<T>)
        const isSelectedFilter = selectedFilterId === id

        await deleteFilter({ id })

        if (isSelectedFilter) {
          form.reset(initialValues)
          replace(`${window.location.pathname}${window.location.hash}`, { scroll: false })
        }
      } catch (error) {
        console.error('Error deleting filter:', error)
      }
    },
    [form, replace, initialValues, deleteFilter],
  )

  const handleSavedFilterChange = useCallback(
    (filterId: string | null) => {
      const selectedFilter = savedFilters?.find((filter) => Number(filter.id) === Number(filterId))
      if (!selectedFilter) return

      const { filters } = selectedFilter
      const transformedFilters = transformFilters<typeof filters>(filters)

      // Saved filters can carry keys from fields the config has since removed or
      // renamed (e.g. legacy transaction psp/subtype/transaction_type after the
      // BalanceTransaction migration). Apply only keys still declared in the
      // current config so stale keys don't leak back into query params on submit.
      const allowedKeys = new Set(Object.keys(initialValues ?? {}))
      const allowedFilters = Object.fromEntries(
        Object.entries(transformedFilters).filter(([key]) => allowedKeys.has(key)),
      )

      form.reset({ ...initialValues, ...allowedFilters } as DefaultValues<T>)
    },
    [form, savedFilters, initialValues],
  )

  return useMemo(
    () => ({
      handleSaveFilter,
      handleDeleteFilter,
      savedFilters,
      handleSavedFilterChange,
    }),
    [handleDeleteFilter, handleSaveFilter, handleSavedFilterChange, savedFilters],
  )
}
